import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  Activity,
  AlertTriangle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { getHealthTrends, getHealthSummary, getFamilyMembers } from '@/services/api';
import { toast } from 'sonner';

const COLORS = ['#1e3a8a', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function AnalyticsPage() {
  const [trends, setTrends] = useState({});
  const [summary, setSummary] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedMember]);

  const loadData = async () => {
    setLoading(true);
    try {
      const memberId = selectedMember !== 'all' ? selectedMember : null;
      const [trendsData, summaryData, membersData] = await Promise.all([
        getHealthTrends(memberId),
        getHealthSummary(),
        getFamilyMembers()
      ]);
      setTrends(trendsData);
      setSummary(summaryData);
      setFamilyMembers(membersData);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const metricKeys = Object.keys(trends);
  const filteredMetrics = selectedMetric === 'all' 
    ? metricKeys 
    : metricKeys.filter(k => k === selectedMetric);

  const parseNormalRange = (normalRange) => {
    if (!normalRange) return null;
    const matches = String(normalRange).match(/-?\d*\.?\d+/g);
    if (!matches || matches.length === 0) return null;
    const numbers = matches.map(Number).filter((val) => !Number.isNaN(val));
    if (numbers.length >= 2) {
      const [min, max] = numbers;
      return { min: Math.min(min, max), max: Math.max(min, max) };
    }
    return { value: numbers[0] };
  };

  const prepareChartData = (metricName) => {
    const data = trends[metricName] || [];
    const firstRange = data.find(item => item.normal_range)?.normal_range;
    const parsedRange = parseNormalRange(firstRange);

    return data.map(item => ({
      date: item.date,
      value: item.value,
      status: item.status,
      normalMin: parsedRange?.min ?? null,
      normalMax: parsedRange?.max ?? null,
      normalValue: parsedRange?.value ?? null
    }));
  };

  const getLatestTrend = (metricName) => {
    const data = trends[metricName] || [];
    if (data.length < 2) return 'stable';
    const latest = data[data.length - 1].value;
    const previous = data[data.length - 2].value;
    if (latest > previous * 1.05) return 'up';
    if (latest < previous * 0.95) return 'down';
    return 'stable';
  };

  const TrendIcon = ({ trend }) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const totalDataPoints = Object.values(trends).reduce((sum, arr) => sum + arr.length, 0);
  const normalDataPoints = Object.values(trends).reduce((sum, arr) => {
    return sum + arr.filter(item => item.status === 'normal').length;
  }, 0);
  const normalRate = totalDataPoints ? Math.round((normalDataPoints / totalDataPoints) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Health Analytics
            </h1>
            <p className="text-slate-600 mt-1">Track your health metrics over time</p>
          </div>
          
          <div className="flex gap-3">
            {familyMembers.length > 0 && (
              <Select value={selectedMember} onValueChange={setSelectedMember}>
                <SelectTrigger className="w-40" data-testid="member-filter">
                  <SelectValue placeholder="All Members" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  {familyMembers.map((member) => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Select value={selectedMetric} onValueChange={setSelectedMetric}>
              <SelectTrigger className="w-48" data-testid="metric-filter">
                <SelectValue placeholder="All Metrics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Metrics</SelectItem>
                {metricKeys.map((metric) => (
                  <SelectItem key={metric} value={metric}>{metric}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Total Records</p>
              <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {summary?.total_records || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Metrics Tracked</p>
              <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {metricKeys.length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Data Points</p>
              <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {totalDataPoints}
              </p>
            </CardContent>
          </Card>
          <Card className={`bg-white border-slate-100 shadow-sm ${summary?.abnormal_findings?.length > 0 ? 'border-red-200' : ''}`}>
            <CardContent className="p-5">
              <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Alerts</p>
              <p className={`text-3xl font-bold mt-2 ${summary?.abnormal_findings?.length > 0 ? 'text-red-600' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                {summary?.abnormal_findings?.length || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-100 shadow-sm" data-testid="normal-range-card">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500 font-medium uppercase tracking-wide">Within Normal Range</p>
              <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {normalRate}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Trend Charts */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton h-80 rounded-xl" />
            ))}
          </div>
        ) : metricKeys.length === 0 ? (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="py-16 text-center">
              <Activity className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium text-lg">No health data to display</p>
              <p className="text-slate-400 mt-2">Upload health records to start tracking your metrics</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredMetrics.slice(0, 8).map((metricName, index) => {
              const chartData = prepareChartData(metricName);
              const trend = getLatestTrend(metricName);
              const latestValue = chartData[chartData.length - 1];
              
              return (
                <Card 
                  key={metricName} 
                  className="bg-white border-slate-200 shadow-sm animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {metricName}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <TrendIcon trend={trend} />
                        {latestValue && (
                          <span className={`text-sm font-semibold ${
                            latestValue.status === 'high' || latestValue.status === 'concerning'
                              ? 'text-red-600'
                              : latestValue.status === 'low'
                                ? 'text-amber-600'
                                : 'text-slate-900'
                          }`}>
                            {latestValue.value} {trends[metricName][0]?.unit || ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56 chart-container rounded-lg p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e2e8f0' }}
                          />
                          <YAxis 
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e2e8f0' }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={COLORS[index % COLORS.length]}
                            strokeWidth={2}
                            fill={`url(#gradient-${index})`}
                          />
                          {chartData.some(point => point.normalMin !== null && point.normalMax !== null) ? (
                            <>
                              <Line
                                type="monotone"
                                dataKey="normalMin"
                                stroke="#94a3b8"
                                strokeDasharray="4 4"
                                dot={false}
                                name="Normal Min"
                              />
                              <Line
                                type="monotone"
                                dataKey="normalMax"
                                stroke="#94a3b8"
                                strokeDasharray="4 4"
                                dot={false}
                                name="Normal Max"
                              />
                            </>
                          ) : chartData.some(point => point.normalValue !== null) ? (
                            <Line
                              type="monotone"
                              dataKey="normalValue"
                              stroke="#94a3b8"
                              strokeDasharray="4 4"
                              dot={false}
                              name="Normal"
                            />
                          ) : null}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Abnormal Findings Alert */}
        {summary?.abnormal_findings?.length > 0 && (
          <Card className="bg-red-50 border-red-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <AlertTriangle className="w-5 h-5" />
                Recent Abnormal Findings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {summary.abnormal_findings.slice(0, 5).map((finding, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-red-100">
                    <span className="text-red-500 mt-0.5">•</span>
                    <div>
                      <p className="text-slate-800">{finding.finding}</p>
                      <p className="text-sm text-slate-500">{finding.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
